import React, { useEffect, useState } from 'react';
import { useSuspenseQuery } from '@apollo/client';
import mustache from 'mustache';
import { useTranslations } from '../../../hooks/useTranslations';
import {
  MomentCalendarTranslationKeysInput,
  TrackingType,
  QueryFormatEddArgs,
  FormatBlockEddQuery,
} from '../../../../../__generated__/loma';
import Panel from '../../ui/Panel/Panel';
import PanelHeader from '../../ui/PanelHeader/PanelHeader';
import { LayoutTree } from '../DynamicLayout';
import {
  useAnalyticsPost,
  NarvarEventTypes,
  NarvarEventComponents,
} from '../../../hooks/Analytics';
import Carousel from '../../ui/Carousel/Carousel';
import useOrderTracking, {
  OrderTrackingQueryData,
} from '../../../hooks/useOrderTracking';
import { usePageContext } from '../../../hooks/PageContext/usePageContext';
import { useTheme } from '../../../hooks/useTheme';
import getFormattedEdd from '../../../../../graphql/getFormattedEdd';
import ShipmentRow from './components/ShipmentRow';

const renderOptions = { escape: (x: string): string => x };

const { FeatureLayout } = LayoutTree;

type ShipmentRowData =
  OrderTrackingQueryData['orderTracking']['orders'][0]['shipments'][0];

type ShipmentRowDataWithDisplayDateFormatted = ShipmentRowData & {
  displayDateFormatted: string[];
};

const getShipmentsToRender = (
  shipments?: ShipmentRowData[],
  mainShipmentId?: string | null,
  trackingType?: TrackingType,
) => {
  if (!shipments || shipments.length === 1) return [];
  // show all shipments, including current shipment for returns
  if (trackingType === 'RETURNS') return shipments;
  if (!mainShipmentId) return [];

  // Since the Shipments block shows "Other Shipments in Your Order",
  // filter out the current shipment based on shipment id
  return shipments.filter(({ shipmentId }) => shipmentId !== mainShipmentId);
};

export interface ShipmentsProps {
  layout?: LayoutTree.FeatureLayout;
  featureName?: string;
  fixedPanelHeight?: string;
}

const eddFormatTranslationKeys: MomentCalendarTranslationKeysInput[] = [
  {
    lastDay: 'packShipmentsDayOfWeekLastDayDateFormat',
    lastWeek: 'packShipmentsDayOfWeekLastWeekDateFormat',
    nextDay: 'packShipmentsDayOfWeekNextDayDateFormat',
    nextWeek: 'packShipmentsDayOfWeekNextWeekDateFormat',
    sameDay: 'packShipmentsDayOfWeekSameDayDateFormat',
    sameElse: 'packShipmentsDayOfWeekSameElseDateFormat',
  },
  {
    lastDay: 'packShipmentsMonthLastDayDateFormat',
    lastWeek: 'packShipmentsMonthLastWeekDateFormat',
    nextDay: 'packShipmentsMonthNextDayDateFormat',
    nextWeek: 'packShipmentsMonthNextWeekDateFormat',
    sameDay: 'packShipmentsMonthSameDayDateFormat',
    sameElse: 'packShipmentsMonthSameElseDateFormat',
  },
  {
    lastDay: 'packShipmentsDayOfMonthLastDayDateFormat',
    lastWeek: 'packShipmentsDayOfMonthLastWeekDateFormat',
    nextDay: 'packShipmentsDayOfMonthNextDayDateFormat',
    nextWeek: 'packShipmentsDayOfMonthNextWeekDateFormat',
    sameDay: 'packShipmentsDayOfMonthWeekSameDayDateFormat',
    sameElse: 'packShipmentsDayOfMonthSameElseDateFormat',
  },
];

const Shipments = ({
  featureName,
  layout = FeatureLayout.PORTRAIT,
  fixedPanelHeight,
}: ShipmentsProps) => {
  const landscape = layout === FeatureLayout.LANDSCAPE;
  const logEvent = useAnalyticsPost();

  const { panelcontainerNoneNone: containerStyle } = useTheme();

  const {
    state: {
      orderNumbers,
      trackingType,
      retailerMoniker,
      localeCountry,
      localeLanguage,
      versionName,
    },
  } = usePageContext();

  const { shipmentsPanelHeader } = useTranslations();

  const { orderTrackingData, mainShipmentId } = useOrderTracking();

  const shipmentsToRender = getShipmentsToRender(
    orderTrackingData?.orders[0].shipments,
    mainShipmentId,
    trackingType,
  );

  const [carouselScrolls, setCarouselScrolls] = useState(0);

  const eddCombos: QueryFormatEddArgs['eddCombos'] = shipmentsToRender.map(
    ({ displayDate }) => ({
      eddFormatTranslationKeys,
      displayDate: displayDate || '',
      dateOnly: false,
    }),
  );

  const { data } = useSuspenseQuery<FormatBlockEddQuery, QueryFormatEddArgs>(
    getFormattedEdd,
    {
      variables: {
        retailerMoniker,
        versionName,
        localeLanguage,
        localeCountry,
        eddCombos,
      },
      skip: shipmentsToRender.length === 0,
    },
  );

  const { formatEdd } = data ?? {};

  useEffect(
    () => {
      const orderNumber = (orderNumbers && orderNumbers[0]) || null;
      const componentHasRendered =
        orderTrackingData &&
        getShipmentsToRender(
          orderTrackingData.orders[0].shipments,
          mainShipmentId,
          trackingType,
        ).length > 0;

      if (!componentHasRendered) return;

      logEvent({
        eventComponent: NarvarEventComponents.NTH_PACK_SHIPMENTS,
        eventFeature: NarvarEventComponents.NTH_PACK_SHIPMENTS,
        eventType: NarvarEventTypes.COMPONENT_RENDER,
        feature: featureName,
        orderNumber,
        pageComponent: NarvarEventComponents.NTH_PACK_SHIPMENTS,
        pageOrderNumber: orderNumber,
        trackLayout: layout,
      });
    },
    // TODO: Make this dependency array exhaustive
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orderTrackingData],
  );

  const shipmentsToRenderWithDisplayDateFormatted: ShipmentRowDataWithDisplayDateFormatted[] =
    shipmentsToRender.map((shipment, i) => ({
      ...shipment,
      displayDateFormatted: formatEdd?.formattedEdds[i] || [],
    }));

  const shipmentsChunks = shipmentsToRenderWithDisplayDateFormatted.reduce(
    (
      result: [
        ShipmentRowDataWithDisplayDateFormatted,
        ShipmentRowDataWithDisplayDateFormatted?,
      ][],
      _,
      index,
      arr,
    ) => {
      if (index % 2 === 0) {
        const [itemChunk0, itemChunk1] = arr.slice(index, index + 2);
        if (itemChunk0) {
          result.push([itemChunk0, itemChunk1]);
        }
      }
      return result;
    },
    [],
  );

  if (shipmentsToRender.length === 0) return null;

  return (
    <Panel
      fixedPanelHeight={fixedPanelHeight}
      header={
        <PanelHeader>
          {mustache.render(
            shipmentsPanelHeader || '',
            {
              shipmentsCount: shipmentsToRender.length,
            },
            undefined,
            renderOptions,
          )}
        </PanelHeader>
      }
      sInner={{ padding: '1.5rem' }}
    >
      <Carousel
        itemsCount={shipmentsChunks.length}
        slickProps={{
          dots: true,
          slidesToScroll: 1,
          slidesToShow: 1,
          infinite: false,
        }}
        carouselControlsContainerStyles={{
          borderTopColor:
            containerStyle && containerStyle.borderTopColor
              ? containerStyle.borderTopColor
              : '',
          borderTopStyle:
            containerStyle && containerStyle.borderTopStyle
              ? containerStyle.borderTopStyle
              : undefined,
          borderTopWidth:
            containerStyle && containerStyle.borderTopWidth
              ? containerStyle.borderTopWidth
              : '',
          padding: '.3rem 0',
          margin: 0,
        }}
        onArrowClick={() => setCarouselScrolls(carouselScrolls + 1)}
      >
        {fixedPanelHeight
          ? shipmentsToRenderWithDisplayDateFormatted.map((shipment, i) => (
              <ShipmentRow
                key={`${shipment.shipmentId || ''}_${i}_shipmentrow`}
                landscape={landscape}
                index={i}
                featureName={featureName}
                shipment={shipment}
                trackingType={trackingType}
                carouselScrolls={carouselScrolls}
              />
            ))
          : shipmentsChunks.map((chunk, i) => (
              <div
                key={`shipmentrow_${chunk[0].shipmentId || ''}_${
                  chunk[1]?.shipmentId || ''
                }_${i}`}
              >
                <ShipmentRow
                  landscape={landscape}
                  index={i * 2}
                  key={`shipmentrow_${chunk[0].shipmentId || ''}_${i * 2}`}
                  featureName={featureName}
                  shipment={chunk[0]}
                  trackingType={trackingType}
                  carouselScrolls={carouselScrolls}
                />
                {chunk[1] && (
                  <ShipmentRow
                    landscape={landscape}
                    index={i * 2 + 1}
                    key={`shipmentrow_${chunk[1].shipmentId || ''}_${
                      i * 2 + 1
                    }`}
                    featureName={featureName}
                    shipment={chunk[1]}
                    trackingType={trackingType}
                    carouselScrolls={carouselScrolls}
                  />
                )}
              </div>
            ))}
      </Carousel>
    </Panel>
  );
};

export default Shipments;
